import { useMutation, useQuery } from '@tanstack/react-query';
import fetcher, { FleetGetListParams, FleetPaginated } from '../fetcher';
import { MasterData } from './master-data';
import { FileData } from './types';

export type AgreementPrintingSearchOptions = {
	searchByOptions: MasterData[];
	plafonTypeOptions: MasterData[];
	frmOptions: MasterData[];
	fbdhOptions: MasterData[];
	activityStatusOptions: MasterData[];
	divisionOptions: MasterData[];
	financingLimitTypeOptions: MasterData[];
};

export const getAgreementPrintingSearchOptions = () => {
	return fetcher<AgreementPrintingSearchOptions>({
		api: '/agreement-printing/get-search-options',
	});
};

export const useGetAgreementPrintingSearchOption = () => {
	return useQuery({
		queryKey: ['getAgreementPrintingSearchOption'] as const,
		queryFn: async () => {
			const res = await getAgreementPrintingSearchOptions();
			return res.data;
		},
	});
};

export type AgreementPrintingItem = {
	id: number;
	napId: number;
	napNo: string;
	spfId: number;
	spfNo: string;
	prospectId: string;
	customerId: string;
	customerName: string;
	bmppGroupName: string;
	frmName: string;
	fbdhName: string;
	divisionName: string;
	plafonTypes: MasterData[];
	financingLimitTypes: MasterData[];
	plafonAmount: number;
	agreementNo: string;
	allowAssignFcdvs: boolean;
	allowUploadAgreement: boolean;
	allowViewAgreement: boolean;
	allowPrintAgreement: boolean;
	agreementDocumentId: number;
	agreementPrintingNo: string;
};
export type SearchAgreementPrintingParam = FleetGetListParams & {
	divisionId?: number;
	plafonTypeId?: number;
	financingLimitTypeId?: number;
	activityStatusId?: string;
	applicationStatusId?: string;
	frmId?: number;
	fbdhId?: number;
};

export const searchAgreementPrinting = (body: SearchAgreementPrintingParam) => {
	return fetcher<FleetPaginated<AgreementPrintingItem>>({
		api: '/agreement-printing/search',
		body,
	});
};

export const useSearchAgreementPrinting = (
	param: SearchAgreementPrintingParam,
) => {
	return useQuery({
		queryKey: ['searchAgreementPrinting', param] as const,
		queryFn: () => searchAgreementPrinting(param),
	});
};

export type AgreementPrintDetail = {
	id: number;
	customerId: string;
	prospectId: string;
	napPlafonTypeId: number;
	resolutionFile: FileData;
	customerName: string;
	spfNo: string;
	spfId: number;
	bmppGroupId: string;
	bmppGroupName: string;
	industryClass: string;
	napId: number;
	napNo: string;
	portfolioGuideline: string;
	ultimateOwner: string;
	rkpDate: string;
	resolutionFullApprovedDate: string;
	resolutionId: number;
	divisionName: string;
	frmName: string;
	fbdhName: string;
	approvalFacilityNotifications: {
		id: number;
		resolutionPlafonId: number;
		wayOfPaymentId: number;
		facilityValidityPeriod: string;
		disbursementMechanism: string;
		financingObjectCondition: string;
		financingObjectEvaluation: string;
		plafonTypeId: number;
		requestedLimit: number;
		approvedLimit: number;
		financingLimitTypeId: number;
		financingTypes: MasterData[];
		facilityTypes: MasterData[];
	}[];
	facilities: {
		id: number;
		assetType: MasterData;
		financingObjectCondition: MasterData;
		financingLimitType?: MasterData;
		plafonType: MasterData;
		tenor: MasterData;
		interestRateIndicationPercent: number;
		downPaymentPercent: number;
		securityDeposit: number;
		administrativeFee: number;
		provisionFee: number;
		serviceFee: number;
		fiduciaryFee: number;
		insurance: MasterData;
		insuranceDiscountPercentage: number | null;
	}[];
	covenants: {
		id: number;
		covenant: string;
		category: MasterData;
		priority: MasterData;
		repeatFrequency: MasterData;
		repeatFrequencyCustom?: {
			id?: number;
			repeatValue: number;
			repeatFrequency: MasterData;
			onPattern: {
				dayOfMonth: number;
				month: MasterData;
			};
			onThePattern: {
				position: MasterData;
				dayOfWeek: MasterData;
				month: MasterData;
			};
		};
		monitoringType: MasterData;
		furtherAnalysis: MasterData;
		dueDate: string;
		uploadDocumentDate: string;
		notes?: string;
		covenantDocument: FileData;
		status: string;
		overdueDays: number;
	}[];
	creditDocumentReviewer: MasterData;
	notes: string;
	approvalHistory: {
		id: number;
		name: string;
		date: string;
		process: string;
		status: string;
		notes: string;
	}[];
	covenantStatusOptions: MasterData[];
	creditDocumentReviewerOptions: MasterData[];
	wayOfPaymentOptions: MasterData[];
	plafonTypeOptions: MasterData[];
	financingLimitTypeOptions: MasterData[];
};

export const getAgreementPrintDetail = (body: string) => {
	return fetcher<AgreementPrintDetail>({
		api: '/agreement-printing/get-detail',
		body,
	});
};

export const useGetAgreementPrintDetail = (id: string) => {
	return useQuery({
		queryKey: ['getAgreementPrintDetail', id] as const,
		queryFn: () => getAgreementPrintDetail(id),
	});
};

export type AgreemenPrintDocumentDetail = {
	id: number;
	agreementDocument: FileData;
	directorStatementDocument: FileData;
	commissionerStatementDocument: FileData;
	otherDocument: FileData;
};

export const getAgreementDocumentDetail = (id: string) => {
	return fetcher<AgreemenPrintDocumentDetail>({
		api: '/agreement-printing/get-agreement-document-detail',
		body: {
			id,
		},
	});
};

export const useGetAgreementDocumentDetail = (id: string) => {
	return useQuery({
		queryKey: ['getAgreementDocumentDetail'] as const,
		queryFn: () => getAgreementDocumentDetail(id),
	});
};

export type PreviewAgreementPrintingPayload = {
	id: number;
};

const previewAgreementPrinting = (body: PreviewAgreementPrintingPayload) => {
	return fetcher<{ signedUrl: string }>({
		api: '/agreement-printing/preview',
		body,
	});
};

export const usePreviewAgreementPrintingMutation = () => {
	return useMutation({
		mutationFn: previewAgreementPrinting,
	});
};

const previewMultipleAgreementPrinting = (
	body: PreviewAgreementPrintingPayload,
) => {
	return fetcher<{ signedUrl: string }[]>({
		api: '/agreement-printing/previews',
		body,
	});
};

export const usePreviewMultpleAgreementPrintingMutation = () => {
	return useMutation({
		mutationFn: previewMultipleAgreementPrinting,
	});
};

export type PrintAgreementPrintingPayload = {
	id: number;
	notes: string;
};

const printAgreementPrinting = (body: PrintAgreementPrintingPayload) => {
	return fetcher<string>({
		api: '/agreement-printing/print',
		body,
	});
};

export const usePrintAgreementPrintingMutation = () => {
	return useMutation({
		mutationFn: printAgreementPrinting,
	});
};

export type UploadAgreementDocumentPayload = {
	id: number;
	agreementDocument: FileData;
	directorStatementDocument: FileData;
	commissionerStatementDocument: FileData;
	otherDocument: FileData;
};

const uploadAgreementDocument = (body: UploadAgreementDocumentPayload) => {
	return fetcher({
		api: '/agreement-printing/upload-agreement-document',
		body,
	});
};

export const useUploadAgreementDocumentMutation = () => {
	return useMutation({
		mutationFn: uploadAgreementDocument,
	});
};

export type SubmitAgreementPrintingPayload = {
	spfId: number;
	covenants: {
		id: number;
		statusId: string;
		dueDate: string;
		notes?: string;
		covenantDocument?: FileData;
	}[];
	creditDocumentReviewerId: number;
	notes?: string;
};

const submitAgreementPrinting = (body: SubmitAgreementPrintingPayload) => {
	return fetcher<{
		signedUrl: string;
	}>({
		api: '/agreement-printing/submit',
		body,
	});
};

export const useSubmitAgreementPrintingMutation = () => {
	return useMutation({
		mutationFn: submitAgreementPrinting,
	});
};

export const getAgreementPrintingToDo = () => {
	return fetcher<number>({
		api: '/agreement-printing/get-things-todo',
	});
};
